import React, { useState, useEffect } from 'react';
import { fetchTirePriceHistory } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import { useChartColors } from '../contexts/ThemeContext';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const TireDetailModal = ({ tire, onClose }) => {
    const [priceHistory, setPriceHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const colors = useChartColors();

    useEffect(() => {
        const loadPriceHistory = async () => {
            if (!tire || !tire.tire_id) return;

            setLoading(true);
            setError(null);

            try {
                const response = await fetchTirePriceHistory(tire.tire_id);
                if (response && response.price_history) {
                    // New format: [{ recorded_at, price }, ...] sorted ascending by time
                    setPriceHistory(response.price_history);
                } else {
                    setPriceHistory([]);
                }
            } catch (err) {
                console.error('Error loading price history:', err);
                setError('Не вдалося завантажити історію цін.');
            } finally {
                setLoading(false);
            }
        };

        loadPriceHistory();
    }, [tire]);

    if (!tire) return null;

    const formatDate = (dateString) => {
        const date = new Date(dateString.replace(' ', 'T'));
        return new Intl.DateTimeFormat('uk-UA', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        }).format(date);
    };

    const formatDateShort = (dateString) => {
        const date = new Date(dateString.replace(' ', 'T'));
        return new Intl.DateTimeFormat('uk-UA', {
            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
        }).format(date);
    };

    const calculatePriceChange = (newPrice, oldPrice) => {
        if (!oldPrice) return 0;
        return ((newPrice - oldPrice) / oldPrice) * 100;
    };

    // Build chart data and stats from the time series
    const chartData = priceHistory.map(p => ({
        time: formatDateShort(p.recorded_at),
        price: p.price,
        fullDate: p.recorded_at
    }));

    const firstPrice = priceHistory.length > 0 ? priceHistory[0].price : null;
    const lastPrice = priceHistory.length > 0 ? priceHistory[priceHistory.length - 1].price : null;
    const minPrice = priceHistory.length > 0 ? Math.min(...priceHistory.map(p => p.price)) : null;
    const maxPrice = priceHistory.length > 0 ? Math.max(...priceHistory.map(p => p.price)) : null;
    const totalChange = (firstPrice !== null && lastPrice !== null) ? lastPrice - firstPrice : 0;
    const totalChangePct = calculatePriceChange(lastPrice, firstPrice);

    // Reverse for the table (newest first)
    const tableRows = [...priceHistory].reverse();

    return (
        <div className="modal-backdrop">
            <div className="modal-content">
                <button className="close-button" onClick={onClose}>×</button>

                <div className="modal-header">
                    <h2>{tire.tire_name}</h2>
                    <div className="tire-id">ID: {tire.tire_id}</div>
                </div>

                <div className="modal-body">
                    <div className="tire-info-columns">
                        <div className="tire-info-column">
                            <div className="info-section">
                                <h3>Характеристики</h3>
                                <div className="info-grid">
                                    <div className="info-label">Бренд</div>
                                    <div className="info-value">{tire.brand_name}</div>

                                    <div className="info-label">Size:</div>
                                    {/* diametr уже містить «R» — додавати не треба */}
                                    <div className="info-value">{tire.width}/{tire.profil} {tire.diametr}</div>

                                    <div className="info-label">Модель</div>
                                    <div className="info-value">{tire.model}</div>

                                    <div className="info-label">Сезон</div>
                                    <div className="info-value">{tire.season}</div>

                                    <div className="info-label">Поточна ціна</div>
                                    <div className="info-value price-value">{tire.price} UAH</div>
                                </div>
                            </div>

                            {tire.link && (
                                <div className="info-section">
                                    <h3>Джерело</h3>
                                    <a
                                        href={"https://infoshina.com.ua/" + tire.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="source-link"
                                    >
                                        Відкрити на сайті
                                    </a>
                                </div>
                            )}
                        </div>

                        <div className="tire-info-column">
                            <div className="info-section">
                                <h3>Історія цін</h3>

                                {loading ? (
                                    <div className="price-history-loading">
                                        <LoadingSpinner />
                                    </div>
                                ) : error ? (
                                    <div className="price-history-error">{error}</div>
                                ) : priceHistory.length > 0 ? (
                                    <>
                                        {/* Chart */}
                                        <div style={{ width: '100%', height: 240, marginBottom: 16 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                                                    <CartesianGrid vertical={false} stroke={colors.line} />
                                                    <XAxis dataKey="time" tick={{ fontSize: 11, fill: colors.text3 }} axisLine={false} tickLine={false} />
                                                    <YAxis tick={{ fontSize: 11, fill: colors.text3 }} domain={['auto', 'auto']} axisLine={false} tickLine={false} />
                                                    <Tooltip
                                                        contentStyle={{ background: colors.surface3, border: 0, borderRadius: 14, color: colors.text }}
                                                        formatter={(value) => [`${value} ₴`, 'Ціна']}
                                                        labelFormatter={(label, payload) => {
                                                            if (payload && payload.length > 0) {
                                                                return formatDate(payload[0].payload.fullDate);
                                                            }
                                                            return label;
                                                        }}
                                                    />
                                                    <Line
                                                        type="monotone"
                                                        dataKey="price"
                                                        stroke={colors.accent}
                                                        strokeWidth={2}
                                                        dot={{ r: 3 }}
                                                        activeDot={{ r: 5 }}
                                                    />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>

                                        {/* Stats */}
                                        <div className="price-history-stats">
                                            <div className="stat">
                                                <span className="stat-label">Перша ціна</span>
                                                <span className="stat-value">{firstPrice} ₴</span>
                                            </div>
                                            <div className="stat">
                                                <span className="stat-label">Поточна</span>
                                                <span className="stat-value">{lastPrice} ₴</span>
                                            </div>
                                            <div className="stat">
                                                <span className="stat-label">Мін / макс</span>
                                                <span className="stat-value">{minPrice} / {maxPrice} ₴</span>
                                            </div>
                                            <div className="stat">
                                                <span className="stat-label">Зміна за весь час</span>
                                                <span className={`stat-value ${totalChange >= 0 ? 'price-increase' : 'price-decrease'}`}>
                                                    {totalChange >= 0 ? '↑' : '↓'}
                                                    {Math.abs(totalChange).toFixed(0)} ₴
                                                    <span className="percent-change">
                                                        ({Math.abs(totalChangePct).toFixed(1)}%)
                                                    </span>
                                                </span>
                                            </div>
                                        </div>

                                        {/* Table of all data points */}
                                        <div className="price-history" style={{ marginTop: 16 }}>
                                            <table className="price-history-table">
                                                <thead>
                                                    <tr>
                                                        <th>Дата</th>
                                                        <th>Ціна</th>
                                                        <th>Зміна</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {tableRows.map((record, index) => {
                                                        // Compare to the previous point chronologically
                                                        const chronologicalIndex = priceHistory.length - 1 - index;
                                                        const prev = chronologicalIndex > 0 ? priceHistory[chronologicalIndex - 1] : null;
                                                        const diff = prev ? record.price - prev.price : 0;
                                                        const changeClass = diff < 0 ? 'price-decrease' : diff > 0 ? 'price-increase' : '';
                                                        return (
                                                            <tr key={index}>
                                                                <td>{formatDate(record.recorded_at)}</td>
                                                                <td>{record.price} ₴</td>
                                                                <td className={changeClass}>
                                                                    {diff !== 0 ? (
                                                                        <>
                                                                            {diff < 0 ? '↓' : '↑'}
                                                                            {Math.abs(diff).toFixed(0)} ₴
                                                                        </>
                                                                    ) : '—'}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                ) : (
                                    <p className="no-history">Історії цін поки немає</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TireDetailModal;
